import { Button, Input, Space } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import request from "@/api";
import { useUserStore } from "@/views/stores";
import WangEditor from "@/views/components/WangEditor";
import "./HelpCenterFormPage.scss";

export interface HelpCenterFormPayload {
    title: string;
    content: string;
    activeFileIds: number[];
    unActiveFileIds: number[];
}

export interface HelpCenterFormDetail {
    id: number;
    title?: string;
    content?: string;
}

export interface HelpCenterFormPageProps {
    loadDetail?: (id: number) => Promise<HelpCenterFormDetail>;
    createHelp?: (payload: HelpCenterFormPayload) => Promise<unknown>;
    updateHelp?: (id: number, payload: HelpCenterFormPayload) => Promise<unknown>;
    permissions?: string[];
}

const EDITOR_FILE_HEADER = /^==ids=(.*?)\n/;

/** 从 Vue 富文本头部读取上一次已关联的文件 ID。 */
export function getHelpCenterStoredFileIds(content?: string): number[] {
    const match = content?.match(EDITOR_FILE_HEADER);
    return (
        match?.[1]
            ?.split("--")
            .map(Number)
            .filter(value => Number.isFinite(value)) || []
    );
}

/** 从富文本媒体 URL 的 #id 后缀读取本次仍在使用的文件 ID。 */
export function getHelpCenterActiveFileIds(content?: string): number[] {
    const activeFileIds: number[] = [];
    const mediaPattern = /<(?:img|video|source)\s+[^>]*src=["'][^"']*#(\d+)["']/gi;
    let match: RegExpExecArray | null;
    while ((match = mediaPattern.exec(content || "")) !== null) {
        const id = Number(match[1]);
        if (id && !activeFileIds.includes(id)) activeFileIds.push(id);
    }
    return activeFileIds;
}

/** 复用 Vue editorManage.setContent 的文件头部格式。 */
export function setHelpCenterEditorContent(content: string, activeFileIds: number[]): string {
    return activeFileIds.length ? `==ids=${activeFileIds.join("--")}\n${content}` : content;
}

/** 复用 Vue 帮助中心详情接口，供编辑态回显。 */
export async function loadHelpCenterFormDetail(id: number): Promise<HelpCenterFormDetail> {
    const response = await request.service.get<HelpCenterFormDetail>(`/dataService/admin/help-center/${id}`);
    return response.data;
}

/** 复用 Vue 帮助中心新增接口。 */
export async function createHelpCenter(payload: HelpCenterFormPayload): Promise<unknown> {
    const response = await request.service.post("/dataService/admin/help-center", payload);
    return response.data;
}

/** 复用 Vue 帮助中心编辑接口。 */
export async function updateHelpCenter(id: number, payload: HelpCenterFormPayload): Promise<unknown> {
    const response = await request.service.put(`/dataService/admin/help-center/${id}`, payload);
    return response.data;
}

/** 帮助中心新增/编辑表单，保留 Vue 的字段校验、回显和提交契约。 */
export function HelpCenterFormPage({
    loadDetail = loadHelpCenterFormDetail,
    createHelp = createHelpCenter,
    updateHelp = updateHelpCenter,
    permissions
}: HelpCenterFormPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const currentPermissions = permissions ?? storePermissions;
    const canWrite = currentPermissions.includes("ROLE_SS_HELP_W");
    const idValue = new URLSearchParams(location.search).get("id");
    const id = idValue ? Number(idValue) : NaN;
    const editing = Number.isFinite(id);
    const [title, setTitle] = useState("");
    const [content, setContent] = useState("");
    const [lastFileIds, setLastFileIds] = useState<number[]>([]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const titleValidationError = errorMessage === "请输入标题";

    useEffect(() => {
        if (!editing) return;
        let disposed = false;
        void loadDetail(id)
            .then(result => {
                if (disposed) return;
                setTitle(result.title || "");
                setLastFileIds(getHelpCenterStoredFileIds(result.content));
                setContent((result.content || "").replace(EDITOR_FILE_HEADER, ""));
                setErrorMessage(null);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("帮助中心详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [editing, id, loadDetail]);

    /** 复用 Vue 表单 payload，后续富文本编辑器可直接填充文件 ID 数组。 */
    const submit = async () => {
        if (!title.trim()) {
            setErrorMessage("请输入标题");
            return;
        }
        setSubmitting(true);
        const payload: HelpCenterFormPayload = {
            title: title.trim(),
            content: setHelpCenterEditorContent(content, getHelpCenterActiveFileIds(content)),
            activeFileIds: getHelpCenterActiveFileIds(content),
            unActiveFileIds: lastFileIds.filter(id => !getHelpCenterActiveFileIds(content).includes(id))
        };
        try {
            if (editing) await updateHelp(id, payload);
            else await createHelp(payload);
            setErrorMessage(null);
            await navigate("/userOperation/lianYunAbout/helpCenter");
        } catch {
            setErrorMessage("帮助中心保存失败");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section aria-label="help-center-form-page" className="help-center-form-page flex h-full min-h-full flex-col">
            {errorMessage && !titleValidationError ? (
                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
            ) : null}
            <div className="help-center-form__fields">
                <div className="help-center-form__title-row">
                    <label className="help-center-form__field-label" htmlFor="help-center-title">
                        <span className="help-center-form__required">*</span> 标题
                    </label>
                    <div className={`help-center-form__field-control ${titleValidationError ? "is-error" : ""}`}>
                        <Input
                            id="help-center-title"
                            aria-label="标题"
                            maxLength={50}
                            placeholder="请输入标题"
                            showCount
                            value={title}
                            onChange={event => {
                                setTitle(event.target.value);
                                if (titleValidationError && event.target.value.trim()) setErrorMessage(null);
                            }}
                        />
                        {titleValidationError ? (
                            <div className="help-center-form__field-error" role="alert">
                                请输入标题
                            </div>
                        ) : null}
                    </div>
                </div>
                <div className="help-center-form__content-row">
                    <div className="help-center-form__content-title">内容</div>
                    <div aria-label="内容" className="help-center-form__editor">
                        <WangEditor
                            height="600px"
                            placeholder="请输入内容..."
                            value={content}
                            onUpdateValue={setContent}
                        />
                    </div>
                </div>
            </div>
            <Space className="operation-fixed-btn" size={0}>
                {canWrite ? (
                    <Button aria-label="提交" loading={submitting} type="primary" onClick={() => void submit()}>
                        提交
                    </Button>
                ) : null}
                <Button onClick={() => navigate(-1)}>返回</Button>
            </Space>
        </section>
    );
}

export default HelpCenterFormPage;
